import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { Button } from "@tellescope/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface InvalidateButtonProps {
  observationIds: string[];
  onShowReasons: () => void;
}

export const InvalidateButton = ({ observationIds, onShowReasons }: InvalidateButtonProps) => {
  const { selectedOrganization } = useAuth();
  const { t } = useTranslation("vitals");
  const [isInvalidating, setIsInvalidating] = useState(false);

  const updateObservationMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "updateOne",
  });

  const requireReason = selectedOrganization?.settings?.endusers?.requireObservationInvalidationReason ?? false;

  const invalidateObservations = async (reason?: string) => {
    setIsInvalidating(true);
    try {
      await Promise.all(
        observationIds.map((id) =>
          updateObservationMutation.mutateAsync({
            id,
            status: "entered-in-error",
            ...(reason ? { invalidationReason: reason } : {}),
          })
        )
      );
    } finally {
      setIsInvalidating(false);
    }
  };

  if (!requireReason) {
    return (
      <Button
        variant="destructive-outline"
        className="h-6 text-xs font-normal rounded-md"
        onClick={() => invalidateObservations()}
        disabled={isInvalidating}
      >
        {isInvalidating ? t("Invalidating...") : t("Invalidate")}
      </Button>
    );
  }

  return (
    <Button
      variant="destructive-outline"
      className="h-6 text-xs font-normal rounded-md"
      onClick={() => onShowReasons()}
    >
      {t("Invalidate")}
    </Button>
  );
};
